/**
 * DropdownListsHub — landing for all admin-managed pick-lists.
 *
 * Currently exposes one card: "قوائم إنشاء الفاتورة" (Invoice creation dropdowns).
 * Future expansion: expense categories, service statuses, etc.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, XCircle, ChevronRight, Tags, Briefcase } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { usePerms } from '../../permissions/PermissionsContext';

export default function DropdownListsHub() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const cards = [
    {
      id: 'withdrawal-rejection',
      titleAr: 'قائمة أسباب رفض طلبات السحب',
      titleEn: 'Withdrawal Rejection Reasons',
      descAr: 'إدارة الأسباب المعروضة عند رفض طلب سحب من التاجر (عربي / إنجليزي + ترجمة AI)',
      descEn: 'Manage the reasons shown when rejecting a merchant withdrawal (AR/EN + AI translate)',
      icon: XCircle,
      color: '#f87171',
      route: '/dashboard/finance/settings/dropdown-lists/withdrawal-rejection',
    },
    {
      id: 'invoice',
      titleAr: 'قوائم إنشاء الفاتورة (إصدار قديم)',
      titleEn: 'Invoice Creation Dropdowns (Legacy)',
      descAr: 'قائمة قديمة بأسماء الخدمة المسطّحة. تُستخدم للتوافق فقط.',
      descEn: 'Legacy flat service-type list kept for back-compat only.',
      icon: FileText,
      color: '#9ca3af',
      route: '/dashboard/finance/settings/dropdown-lists/invoice',
    },
    {
      id: 'invoice-hierarchical',
      titleAr: 'قوائم إنشاء الفاتورة (رئيسي + فرعي)',
      titleEn: 'Invoice Dropdowns (Main + Sub)',
      descAr: 'اسم الخدمة الرئيسية + الفرعية مع اختيار المنصات (عربي/إنجليزي + ترجمة AI). تُستخدم في نموذج إنشاء الفاتورة.',
      descEn: 'Main & sub service names with multi-platform picker (AR/EN + AI translate). Used in the invoice form.',
      icon: FileText,
      color: '#d4af37',
      route: '/dashboard/finance/settings/dropdown-lists/invoice-hierarchical',
    },
    // iter-218m (Feb 2026) — Relocated from Control-Panel Settings and
    // Finance Settings root, respectively, so both live-together in one
    // "dropdown lists" hub.
    {
      id: 'entity-types',
      titleAr: 'أنواع الجهات',
      titleEn: 'Entity Types',
      descAr: 'قائمة أنواع جهات الدفع (تُستخدم في نموذج جهات الدفع الإلزامي)',
      descEn: 'Entity type list (used by the mandatory Payment Entity picker)',
      icon: Tags,
      color: '#22D3EE',
      route: '/control-panel-settings/dropdowns/entity-types?from=finance',
    },
    {
      id: 'payment-entities',
      titleAr: 'جهات الدفع وأسعار الصرف',
      titleEn: 'Payment Entities & Rates',
      descAr: 'إدارة قائمة جهات الدفع (جوجل، AWS، …) مع العملة وسعر الصرف مقابل الدرهم',
      descEn: 'Manage payment entities (Google, AWS, …) with currency + AED exchange rate',
      icon: Briefcase,
      color: '#F59E0B',
      route: '/dashboard/finance/settings/payment-entities',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'القوائم المنسدلة', label_en: 'Dropdown Lists', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('القوائم المنسدلة', 'Dropdown Lists')}
          </h1>
          <p className="text-white/50 text-sm">
            {t('إدارة القوائم المنسدلة المستخدمة في نماذج القسم المالي',
               'Manage the dropdown lists used across Finance forms')}
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visiblePages(cards, 'route').map((c) => (
            <motion.button
              key={c.id}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(c.route)}
              className="group relative bg-white/[0.02] rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/40 text-start"
              data-testid={`dropdown-lists-card-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: `${c.color}15`, border: `1px solid ${c.color}30` }}>
                  <c.icon className="w-7 h-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-1">{isAr ? c.titleAr : c.titleEn}</h3>
                  <p className="text-white/60 text-xs leading-relaxed">{isAr ? c.descAr : c.descEn}</p>
                </div>
                <ChevronRight className={`w-5 h-5 text-white/40 group-hover:text-[#d4af37] ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </motion.button>
          ))}
        </motion.div>
      </main>
    </div>
  );
}
